import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { formatMinor } from '@eprintsmart/shared';
import { getSettlement, type SettlementView } from '../services/jobs';

/** Last twelve months, newest first. */
function recentPeriods(now = new Date()): { value: string; label: string }[] {
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    return {
      value: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
    };
  });
}

export function Settlements() {
  const periods = recentPeriods();
  const [period, setPeriod] = useState(periods[0]!.value);
  const [data, setData] = useState<SettlementView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    getSettlement(period)
      .then((result) => { if (!cancelled) setData(result); })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load that statement.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [period]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="h1" sx={{ flexGrow: 1 }}>Settlements</Typography>
        <TextField
          size="small"
          select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          sx={{ width: 200 }}
        >
          {periods.map((p) => (
            <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
          ))}
        </TextField>
      </Stack>

      <Typography variant="body2" color="text.secondary">
        Every job you dispatched in the selected month. Figures are computed from the jobs
        themselves, so the total below always matches the lines above it.
      </Typography>

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress /></Box>
      ) : !data || data.lines.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Nothing dispatched in this month.</Typography>
        </Paper>
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Dispatched</TableCell>
                <TableCell>Order</TableCell>
                <TableCell>Job</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.lines.map((line) => (
                <TableRow key={line.jobId}>
                  <TableCell>
                    {line.dispatchedAt
                      ? new Date(line.dispatchedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
                      : '—'}
                  </TableCell>
                  <TableCell>{line.orderNumber}</TableCell>
                  <TableCell>{line.productName}</TableCell>
                  <TableCell align="right">{line.qty}</TableCell>
                  <TableCell align="right">{formatMinor(line.amountMinor)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={3} />
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {data.settlement.jobCount} jobs
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                  {formatMinor(data.settlement.netPayableMinor)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}
